import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Divider,
  Alert,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import {
  AccountCircle,
  Security,
  History,
  Help,
  Speed,
  NotificationsActive,
  Save,
  Edit,
  Visibility,
  VisibilityOff,
  Delete,
  Info,
  CheckCircle,
  ErrorOutline,
  AccessTime,
  Business,
  Email,
  Phone,
  Web,
  Description,
  Key,
  ContentCopy,
  Refresh,
  Add,
  Psychology,
  SmartToy,
  Person,
  PhotoCamera,
  Cancel,
  LocationOn,
  Language,
  Shield,
  Smartphone
} from '@mui/icons-material';
import CoPilotSettingsTab from '../components/CoPilot/CoPilotSettingsTab';
import { AIConfigPanel } from '../components/AI';
import { PermissionGuard } from '../components/Permissions/PermissionGuard'; // 14/11/2025 - User Access Management Module Phase 5.2.3
import { formatApiTokenError } from '../utils/errorParser';
import AvatarUpload from '../components/Avatar/AvatarUpload';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setUser } from '../store/slices/authSlice';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const SettingsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  // Parse tab from URL query parameters
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [profileSubTab, setProfileSubTab] = useState(0); // For My Profile sub-tabs
  const [profileEditing, setProfileEditing] = useState(false); // For editing state in profile
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [auditLogDetailOpen, setAuditLogDetailOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<any>(null);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [scheduleDemoOpen, setScheduleDemoOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [slaData, setSlaData] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [teamPerformanceData, setTeamPerformanceData] = useState<any[]>([]);
  const [performanceDateRange, setPerformanceDateRange] = useState<'7days' | '30days' | 'custom'>('7days');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [slaConfigDialogOpen, setSlaConfigDialogOpen] = useState(false);
  const [editingSlaField, setEditingSlaField] = useState<string>('');
  const [slaConfig, setSlaConfig] = useState({
    firstResponseTime: '5',
    resolutionTime: '2',
    csatTarget: '90',
    workingHours: '9 AM - 6 PM (UTC)'
  });
  
  // API Token states
  const [apiTokens, setApiTokens] = useState<any[]>([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showCreateToken, setShowCreateToken] = useState(false);
  const [currentToken, setCurrentToken] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [tokenExpiry, setTokenExpiry] = useState('30');
  const [showTokenValue, setShowTokenValue] = useState(false);
  
  const [systemInfo, setSystemInfo] = useState({
    version: '',
    instanceId: '',
    region: '',
    status: 'loading'
  });
  
  const [accountData, setAccountData] = useState({
    name: '',
    domain: '',
    supportEmail: '',
    locale: 'en',
    timezone: 'UTC',
    autoResolve: true,
    emailNotifications: true,
    inactivityTimeout: 30,
    workingHours: '9 AM - 6 PM'
  });

  const [originalAccountData, setOriginalAccountData] = useState({});

  // Update tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 7) {
        setSelectedTab(tabIndex);
      }
    }
  }, [location.search]);

  // Agent profile state
  const [agentProfileData, setAgentProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    display_name: '',
    email: user?.email || '',
    phone_number: '',
    avatar_url: user?.avatar_url || '',
    timezone: 'UTC',
    language: 'en'
  });

  const [profileSaving, setProfileSaving] = useState(false);

  // Personal Info state for My Profile tab
  const [personalInfo, setPersonalInfo] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    phone: '',
    location: '',
  });

  // Company Details state for My Profile tab
  const [companyDetails, setCompanyDetails] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    website: '',
  });

  // Password change state for My Profile tab
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Load agent profile when user changes
  useEffect(() => {
    if (user) {
      setAgentProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        display_name: '',
        email: user.email || '',
        phone_number: '',
        avatar_url: user.avatar_url || '',
        timezone: 'UTC',
        language: 'en'
      });
      loadCurrentAgentProfile();
    }
  }, [user?.id]);

  // Load data on component mount
  useEffect(() => {
    loadAccountData();
    loadSLAData();
    loadAuditLogs();
    loadSystemInfo();
    loadApiTokens();
    loadTeamPerformance();
  }, []);

  // Reload SLA data when date range changes
  useEffect(() => {
    if (selectedTab === 4) { // Only reload when on SLA & Performance tab
      loadSLAData();
      loadTeamPerformance();
    }
  }, [performanceDateRange, customDateRange]);

  // Load current agent's full profile
  const loadCurrentAgentProfile = async () => {
    if (!user?.id) return;

    try {
      const response = await api.get(`/agents/${user.id}`);
      const agent = response.data;

      setAgentProfileData({
        first_name: agent.first_name || '',
        last_name: agent.last_name || '',
        display_name: agent.display_name || '',
        email: agent.email || '',
        phone_number: agent.phone_number || '',
        avatar_url: agent.avatar_url || '',
        timezone: agent.timezone || 'UTC',
        language: agent.language || 'en'
      });
    } catch (error) {
      console.error('Failed to load agent profile:', error);
    }
  };

  // Save agent profile
  const handleSaveAgentProfile = async () => {
    if (!user?.id) return;

    setProfileSaving(true);
    try {
      await api.patch(`/agents/${user.id}`, agentProfileData);

      // Update Redux store with new user data
      dispatch(setUser({
        ...user,
        first_name: agentProfileData.first_name,
        last_name: agentProfileData.last_name,
        full_name: `${agentProfileData.first_name} ${agentProfileData.last_name}`,
        email: agentProfileData.email,
        avatar_url: agentProfileData.avatar_url
      }));

      setSnackbarMessage('Profile updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to save agent profile:', error);
      setSnackbarMessage('Failed to save profile. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle Personal Info Save
  const handlePersonalInfoSave = async () => {
    setProfileSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSnackbarMessage('Personal information updated successfully!');
      setSnackbarOpen(true);
      setProfileEditing(false);
    } catch (error) {
      console.error('Failed to save personal info:', error);
      setSnackbarMessage('Failed to save personal information.');
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle Password Change
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbarMessage('New passwords do not match!');
      setSnackbarOpen(true);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setSnackbarMessage('Password must be at least 8 characters long!');
      setSnackbarOpen(true);
      return;
    }

    setProfileSaving(true);
    try {
      await api.post('/password/change-password', {
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      setSnackbarMessage('Password changed successfully!');
      setSnackbarOpen(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      setSnackbarMessage(error.response?.data?.detail || 'Failed to change password.');
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Load existing API tokens
  const loadApiTokens = async () => {
    setTokenLoading(true);
    try {
      const accessToken = localStorage.getItem('token');
      console.log('🔐 Access token for API tokens request:', accessToken ? `${accessToken.substring(0, 20)}...` : 'NOT FOUND');
      
      if (!accessToken) {
        console.error('❌ No access token found in localStorage');
        setApiTokens([]);
        setTokenLoading(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api-tokens/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      console.log('📡 API tokens request response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Loaded API tokens from backend:', data);
        setApiTokens(data || []); // Fixed: data is the array directly, not data.tokens
      } else {
        console.error('❌ Failed to load API tokens from backend:', response.status);
        const errorText = await response.text();
        console.error('❌ Error details:', errorText);
        setApiTokens([]); // Don't show demo data - force real backend integration
      }
    } catch (error) {
      console.error('❌ Error loading API tokens:', error);
      setApiTokens([]); // Don't show demo data - force real backend integration
    } finally {
      setTokenLoading(false);
    }
  };

  // Generate new API token
  const generateApiToken = async () => {
    if (!tokenName.trim()) {
      setSnackbarMessage('Please provide a token name');
      setSnackbarOpen(true);
      return;
    }

    setTokenLoading(true);
    try {
      const accessToken = localStorage.getItem('token');
      console.log('🔐 Access token for token creation:', accessToken ? `${accessToken.substring(0, 20)}...` : 'NOT FOUND');
      
      if (!accessToken) {
        throw new Error('No access token found. Please login again.');
      }
      
      console.log('📝 Creating API token via backend...');
      
      // Call the proper API token creation endpoint
      // const response = await fetch('http://localhost:8001/api/v1/api-tokens/', {
      // const response = await fetch('http://localhost:8001/api/v1/api-tokens/', {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api-tokens/`, {		  
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          name: tokenName,
          expires_in_days: parseInt(tokenExpiry),
          description: `Generated from web UI on ${new Date().toLocaleDateString()}`
        })
      });

      console.log('📡 API token creation response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API token creation failed:', response.status, errorText);
        throw new Error(errorText || `Failed to create API token (${response.status})`);
      }

      const data = await response.json();
      console.log('✅ API token created successfully:', data);
      
      // Store the new token for display
      setCurrentToken(data.access_token);
      setShowTokenValue(true);
      
      // Clear form
      setTokenName('');
      setShowCreateToken(false);
      
      // Refresh the tokens list from backend to show the newly created token
      await loadApiTokens();
      
      setSnackbarMessage('API token generated successfully and stored in database! Copy it now - you won\'t see it again.');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Error generating API token:', error);
      const errorMessage = formatApiTokenError(error, 'generate');
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setTokenLoading(false);
    }
  };

  // Copy token to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage('Copied to clipboard!');
      setSnackbarOpen(true);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbarMessage('Copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  // Revoke API token
  const revokeApiToken = async (tokenId: number) => {
    if (!window.confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('Revoking API token via backend:', tokenId);
      
      // const response = await fetch(`http://localhost:8001/api/v1/api-tokens/${tokenId}`, {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api-tokens/${tokenId}`, {		  
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to revoke token');
      }

      console.log('API token revoked successfully in backend');
      
      // Refresh the tokens list from backend
      await loadApiTokens();
      
      setSnackbarMessage('API token revoked successfully in database');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error revoking API token:', error);
      const errorMessage = formatApiTokenError(error, 'revoke');
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    }
  };

  const loadAccountData = async () => {
    try {
      const savedData = localStorage.getItem('converse_account_settings');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setAccountData(parsedData);
        setOriginalAccountData(parsedData);
      } else {
        const defaultData = {
          name: 'Acme Corp',
          domain: 'acme.converse.com',
          supportEmail: 'support@acme.com',
          locale: 'en',
          timezone: 'UTC',
          autoResolve: true,
          emailNotifications: true,
          inactivityTimeout: 30,
          workingHours: '9 AM - 6 PM'
        };
        setAccountData(defaultData);
        setOriginalAccountData(defaultData);
      }
    } catch (error) {
      console.error('Failed to load account data:', error);
    }
  };

  const loadSLAData = async () => {
    setLoading(true);
    try {
      // Calculate date range based on selection
      const endDate = new Date();
      const startDate = new Date();

      if (performanceDateRange === '7days') {
        startDate.setDate(endDate.getDate() - 7);
      } else if (performanceDateRange === '30days') {
        startDate.setDate(endDate.getDate() - 30);
      } else if (customDateRange.start && customDateRange.end) {
        startDate.setTime(new Date(customDateRange.start).getTime());
        endDate.setTime(new Date(customDateRange.end).getTime());
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await api.get(`/advanced-reports/sla-compliance?start_date=${startDateStr}&end_date=${endDateStr}`);

      if (response.data) {
        // Transform backend data to frontend format
        const transformed = [
          {
            metric: 'First Response Time',
            target: `< ${slaConfig.firstResponseTime} min`,
            current: `${response.data.avg_first_response_time?.toFixed(1) || 0} min`,
            status: (response.data.first_response_sla_compliance || 0) >= 95 ? 'excellent' :
                    (response.data.first_response_sla_compliance || 0) >= 80 ? 'good' : 'needs-improvement',
            percentage: Math.round(response.data.first_response_sla_compliance || 0)
          },
          {
            metric: 'Resolution Time',
            target: `< ${slaConfig.resolutionTime} hrs`,
            current: `${response.data.avg_resolution_time?.toFixed(1) || 0} hrs`,
            status: (response.data.resolution_sla_compliance || 0) >= 90 ? 'excellent' :
                    (response.data.resolution_sla_compliance || 0) >= 75 ? 'good' : 'needs-improvement',
            percentage: Math.round(response.data.resolution_sla_compliance || 0)
          },
          {
            metric: 'Customer Satisfaction',
            target: `> ${slaConfig.csatTarget}%`,
            current: `${response.data.average_csat_score?.toFixed(0) || 0}%`,
            status: (response.data.average_csat_score || 0) >= 90 ? 'excellent' :
                    (response.data.average_csat_score || 0) >= 75 ? 'good' : 'needs-improvement',
            percentage: Math.round(response.data.average_csat_score || 0)
          },
          {
            metric: 'Response Rate',
            target: '> 95%',
            current: `${response.data.response_rate?.toFixed(0) || 0}%`,
            status: (response.data.response_rate || 0) >= 95 ? 'excellent' :
                    (response.data.response_rate || 0) >= 85 ? 'good' : 'needs-improvement',
            percentage: Math.round(response.data.response_rate || 0)
          },
        ];
        setSlaData(transformed);
      }
    } catch (error) {
      console.error('Failed to load SLA data:', error);
      // Fallback to default values if API fails
      setSlaData([
        { metric: 'First Response Time', target: '< 5 min', current: 'N/A', status: 'needs-improvement', percentage: 0 },
        { metric: 'Resolution Time', target: '< 2 hrs', current: 'N/A', status: 'needs-improvement', percentage: 0 },
        { metric: 'Customer Satisfaction', target: '> 90%', current: 'N/A', status: 'needs-improvement', percentage: 0 },
        { metric: 'Response Rate', target: '> 95%', current: 'N/A', status: 'needs-improvement', percentage: 0 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamPerformance = async () => {
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();

      if (performanceDateRange === '7days') {
        startDate.setDate(endDate.getDate() - 7);
      } else if (performanceDateRange === '30days') {
        startDate.setDate(endDate.getDate() - 30);
      } else if (customDateRange.start && customDateRange.end) {
        startDate.setTime(new Date(customDateRange.start).getTime());
        endDate.setTime(new Date(customDateRange.end).getTime());
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await api.get(`/advanced-reports/team-analytics?start_date=${startDateStr}&end_date=${endDateStr}`);

      if (response.data && response.data.team_performance) {
        const transformedData = response.data.team_performance.map((member: any) => ({
          name: member.agent_name || 'Unknown',
          conversations: member.conversations_handled || 0,
          responseTime: `${member.avg_response_time?.toFixed(1) || 0} min`,
          resolutionTime: `${member.avg_resolution_time?.toFixed(1) || 0} hrs`,
          rating: member.csat_score?.toFixed(1) || 0,
          status: (member.csat_score || 0) >= 4.5 ? 'excellent' :
                  (member.csat_score || 0) >= 4.0 ? 'good' : 'needs-improvement'
        }));
        setTeamPerformanceData(transformedData);
      }
    } catch (error) {
      console.error('Failed to load team performance:', error);
      // Fallback data
      setTeamPerformanceData([]);
    }
  };

  const loadAuditLogs = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setAuditLogs([
        { id: 1, action: 'User Login', user: 'John Doe', timestamp: new Date().toISOString(), ip: '192.168.1.1', status: 'success', details: 'Successful login from Chrome browser' },
        { id: 2, action: 'API Token Generated', user: 'Admin User', timestamp: new Date(Date.now() - 300000).toISOString(), ip: '192.168.1.2', status: 'success', details: 'New API token created: MCP Integration' },
        { id: 3, action: 'Settings Updated', user: 'Admin User', timestamp: new Date(Date.now() - 600000).toISOString(), ip: '192.168.1.3', status: 'success', details: 'Account settings updated: notification preferences changed' },
      ]);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  };

  // Handler for date range button clicks
  const handleDateRangeChange = (range: '7days' | '30days' | 'custom') => {
    if (range === 'custom') {
      // For custom range, we'll need to open a date picker dialog
      // For now, just set it and user can modify customDateRange state
      setPerformanceDateRange(range);
    } else {
      setPerformanceDateRange(range);
    }
  };

  // Handler for SLA configuration edit
  const handleSLAConfigEdit = (field: string) => {
    setEditingSlaField(field);
    setSlaConfigDialogOpen(true);
  };

  // Handler for saving SLA configuration
  const handleSaveSLAConfig = async () => {
    try {
      setSaving(true);
      // In a real app, save to backend
      // await api.post('/settings/sla-config', slaConfig);
      setSlaConfigDialogOpen(false);
      setSnackbarMessage('SLA configuration updated successfully');
      setSnackbarOpen(true);
      // Reload SLA data with new config
      loadSLAData();
    } catch (error) {
      console.error('Failed to save SLA config:', error);
      setSnackbarMessage('Failed to save SLA configuration');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const loadSystemInfo = async () => {
    try {
      const version = `v${Math.floor(Math.random() * 3) + 2}.${Math.floor(Math.random() * 20)}.${Math.floor(Math.random() * 10)}`;
      const instanceId = `conv-${Math.random().toString(36).substr(2, 9)}`;
      const regions = ['US East', 'US West', 'Europe', 'Asia Pacific'];
      const region = regions[Math.floor(Math.random() * regions.length)];
      
      setSystemInfo({
        version,
        instanceId,
        region,
        status: 'operational'
      });
    } catch (error) {
      console.error('Failed to load system info:', error);
      setSystemInfo(prev => ({ ...prev, status: 'error' }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      localStorage.setItem('converse_account_settings', JSON.stringify(accountData));
      await new Promise(resolve => setTimeout(resolve, 1500));
      setOriginalAccountData(accountData);
      setSnackbarMessage('Settings saved successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to save settings. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Settings
      </Typography>

      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={handleTabChange} aria-label="settings tabs" variant="scrollable" scrollButtons="auto">
            <Tab
              icon={<Person />}
              label="My Profile"
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              icon={<AccountCircle />}
              label="Account Settings"
              iconPosition="start"
              {...a11yProps(1)}
            />
            <Tab
              icon={<Key />}
              label="API Tokens"
              iconPosition="start"
              {...a11yProps(2)}
            />
            <Tab
              icon={<History />}
              label="Audit Logs"
              iconPosition="start"
              {...a11yProps(3)}
            />
            <Tab
              icon={<Speed />}
              label="SLA & Performance"
              iconPosition="start"
              {...a11yProps(4)}
            />
            <Tab
              icon={<Help />}
              label="Help & Support"
              iconPosition="start"
              {...a11yProps(5)}
            />
            <Tab
              icon={<Psychology />}
              label="Co-Pilot"
              iconPosition="start"
              {...a11yProps(6)}
            />
            <Tab
              icon={<SmartToy />}
              label="AI Configuration"
              iconPosition="start"
              {...a11yProps(7)}
            />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* My Profile Tab (index 0) - Full multi-tab profile management */}
          <TabPanel value={selectedTab} index={0}>
            {/* Profile Header Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <AvatarUpload
                      currentAvatar={agentProfileData.avatar_url}
                      onAvatarChange={(url) => setAgentProfileData(prev => ({ ...prev, avatar_url: url || '' }))}
                      name={`${agentProfileData.first_name} ${agentProfileData.last_name}`.trim()}
                      size={100}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" fontWeight="bold">
                      {user?.full_name || 'User Name'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {user?.email}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip label={user?.role?.replace('_', ' ').toUpperCase() || 'Agent'} color="primary" size="small" />
                      <Chip label="Active" color="success" size="small" />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Profile Sub-Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs value={profileSubTab} onChange={(e, v) => setProfileSubTab(v)} variant="fullWidth">
                <Tab icon={<Person />} iconPosition="start" label="Personal Info" />
                <Tab icon={<Business />} iconPosition="start" label="Company Details" />
                <Tab icon={<Security />} iconPosition="start" label="Security" />
              </Tabs>
            </Paper>

            {/* Personal Info Tab */}
            {profileSubTab === 0 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Personal Information
                    </Typography>
                    {!profileEditing ? (
                      <Button
                        startIcon={<Edit />}
                        variant="outlined"
                        onClick={() => setProfileEditing(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<Save />}
                          variant="contained"
                          onClick={handlePersonalInfoSave}
                          disabled={profileSaving}
                        >
                          {profileSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          startIcon={<Cancel />}
                          variant="outlined"
                          onClick={() => setProfileEditing(false)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={personalInfo.name}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, name: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={personalInfo.email}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={personalInfo.phone}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Location"
                        value={personalInfo.location}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, location: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Company Details Tab */}
            {profileSubTab === 1 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Company Details
                    </Typography>
                    {!profileEditing ? (
                      <Button
                        startIcon={<Edit />}
                        variant="outlined"
                        onClick={() => setProfileEditing(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<Save />}
                          variant="contained"
                          onClick={handlePersonalInfoSave}
                          disabled={profileSaving}
                        >
                          {profileSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          startIcon={<Cancel />}
                          variant="outlined"
                          onClick={() => setProfileEditing(false)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={companyDetails.companyName}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, companyName: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Industry"
                        value={companyDetails.industry}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, industry: e.target.value })}
                        disabled={!profileEditing}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Size"
                        value={companyDetails.companySize}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, companySize: e.target.value })}
                        disabled={!profileEditing}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        value={companyDetails.website}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, website: e.target.value })}
                        disabled={!profileEditing}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Web />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Security Tab */}
            {profileSubTab === 2 && (
              <Card>
                <CardContent>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Ensure your password is strong and unique to protect your account.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type={showCurrentPassword ? 'text' : 'password'}
                        label="Current Password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Key />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type={showNewPassword ? 'text' : 'password'}
                        label="New Password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Key />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type={showConfirmPassword ? 'text' : 'password'}
                        label="Confirm New Password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Key />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.
                      </Alert>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        startIcon={profileSaving ? <CircularProgress size={16} /> : <Save />}
                        onClick={handlePasswordChange}
                        disabled={profileSaving}
                        size="large"
                      >
                        {profileSaving ? 'Changing Password...' : 'Change Password'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </TabPanel>

          {/* Account Settings Tab (now index 1) */}
          <TabPanel value={selectedTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="General Information"
                    avatar={<Business color="primary" />}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Account Name"
                        value={accountData.name}
                        onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Domain"
                        value={accountData.domain}
                        onChange={(e) => setAccountData({ ...accountData, domain: e.target.value })}
                        fullWidth
                        InputProps={{ startAdornment: <Web sx={{ mr: 1, color: 'text.secondary' }} /> }}
                      />
                      <TextField
                        label="Support Email"
                        value={accountData.supportEmail}
                        onChange={(e) => setAccountData({ ...accountData, supportEmail: e.target.value })}
                        fullWidth
                        InputProps={{ startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} /> }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Preferences"
                    avatar={<NotificationsActive color="primary" />}
                  />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Auto-resolve conversations"
                          secondary="Automatically resolve conversations after 24 hours of inactivity"
                        />
                        <Switch
                          checked={accountData.autoResolve}
                          onChange={(e) => setAccountData({ ...accountData, autoResolve: e.target.checked })}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Email notifications"
                          secondary="Send email notifications for new conversations and mentions"
                        />
                        <Switch
                          checked={accountData.emailNotifications}
                          onChange={(e) => setAccountData({ ...accountData, emailNotifications: e.target.checked })}
                        />
                      </ListItem>
                    </List>
                    {/* Save button visible for admins and super admins */}
                    {(user?.role === 'administrator' || user?.role === 'super_admin') && (
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                        onClick={handleSaveSettings}
                        disabled={saving}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* API Tokens Tab (now index 2) */}
          <TabPanel value={selectedTab} index={2}>
            <Grid container spacing={3}>
              {/* Current Token Display */}
              {showTokenValue && currentToken && (
                <Grid item xs={12}>
                  <Alert 
                    severity="warning" 
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => setShowTokenValue(false)}
                      >
                        Hide
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      🔐 Your New API Token (save this now!)
                    </Typography>
                    <Box sx={{ 
                      bgcolor: 'rgba(0,0,0,0.1)', 
                      p: 2, 
                      borderRadius: 1, 
                      wordBreak: 'break-all',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      border: '1px dashed rgba(0,0,0,0.3)'
                    }}>
                      {currentToken}
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button 
                        size="small" 
                        startIcon={<ContentCopy />}
                        onClick={() => copyToClipboard(currentToken)}
                      >
                        Copy Token
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => copyToClipboard(`CONVERSE_API_TOKEN=${currentToken}`)}
                      >
                        Copy as .env
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          const accountId = apiTokens[0]?.account_id || 1;
                          const envConfig = `CONVERSE_BASE_URL=https://support.rentremote.com\nCONVERSE_API_TOKEN=${currentToken}\nCONVERSE_ACCOUNT_ID=${accountId}`;
                          copyToClipboard(envConfig);
                        }}
                      >
                        Copy Full Config
                      </Button>
                    </Box>
                  </Alert>
                </Grid>
              )}

              {/* Token Generation */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Generate New API Token"
                    avatar={<Add color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create API tokens for integrating with external applications like the MCP escalation client.
                    </Typography>
                    
                    {!showCreateToken ? (
                      <Button 
                        variant="contained" 
                        onClick={() => setShowCreateToken(true)}
                        startIcon={<Add />}
                      >
                        Generate New Token
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          label="Token Name"
                          value={tokenName}
                          onChange={(e) => setTokenName(e.target.value)}
                          fullWidth
                          placeholder="e.g., MCP Integration, Production Client"
                          helperText="Give your token a descriptive name"
                        />
                        <TextField
                          label="Expires in (days)"
                          value={tokenExpiry}
                          onChange={(e) => setTokenExpiry(e.target.value)}
                          type="number"
                          fullWidth
                          inputProps={{ min: 1, max: 365 }}
                          helperText="Token will expire after this many days"
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="contained" 
                            onClick={generateApiToken}
                            disabled={tokenLoading}
                            startIcon={tokenLoading ? <CircularProgress size={16} /> : <Key />}
                          >
                            {tokenLoading ? 'Generating...' : 'Generate Token'}
                          </Button>
                          <Button 
                            variant="outlined" 
                            onClick={() => {
                              setShowCreateToken(false);
                              setTokenName('');
                              setTokenExpiry('30');
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Quick Setup Guide */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Quick Setup Guide"
                    avatar={<Help color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Use your API token to configure the MCP escalation client:
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="1. Generate API Token" 
                          secondary="Click 'Generate New Token' and copy the value"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="2. Add to .env file" 
                          secondary="CONVERSE_API_TOKEN=your_token_here"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="3. Set Account ID" 
                          secondary={`CONVERSE_ACCOUNT_ID=${apiTokens[0]?.account_id || 1}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="4. Test Connection" 
                          secondary="curl -H 'Authorization: Bearer TOKEN' /api/v1/contacts"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Active Tokens List */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Active API Tokens"
                    action={
                      <Button 
                        onClick={loadApiTokens}
                        startIcon={<Refresh />}
                        disabled={tokenLoading}
                      >
                        Refresh
                      </Button>
                    }
                  />
                  <CardContent>
                    {tokenLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : apiTokens.length === 0 ? (
                      <Alert severity="info">
                        No API tokens found. Generate your first token to get started with integrations.
                      </Alert>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Last Used</TableCell>
                              <TableCell>Expires</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {apiTokens.map((token) => (
                              <TableRow key={token.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Key sx={{ fontSize: 16 }} />
                                    {token.name}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {new Date(token.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  {token.last_used_at 
                                    ? new Date(token.last_used_at).toLocaleDateString()
                                    : 'Never'
                                  }
                                </TableCell>
                                <TableCell>
                                  {token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'Never'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={token.status}
                                    color={token.status === 'active' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Revoke Token">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={() => revokeApiToken(token.id)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Environment Configuration Helper */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Environment Configuration"
                    avatar={<Info color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Copy this configuration to your MCP client's .env file:
                    </Typography>
                    <Box sx={{ 
                      bgcolor: 'rgba(0,0,0,0.05)', 
                      p: 2, 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
{`# Converse Configuration
CONVERSE_BASE_URL=https://support.rentremote.com
CONVERSE_API_TOKEN=<your_generated_token>
CONVERSE_ACCOUNT_ID=${apiTokens[0]?.account_id || 1}

# Database Configuration
MYSQL_HOST=localhost
MYSQL_PORT=3306
MYSQL_USER=root
MYSQL_PASSWORD=your_password
MYSQL_DB=converse_mcp`}
                      </pre>
                    </Box>
                    <Button 
                      sx={{ mt: 1 }}
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(`# Converse Configuration
CONVERSE_BASE_URL=https://support.rentremote.com
CONVERSE_API_TOKEN=<your_generated_token>
CONVERSE_ACCOUNT_ID=${apiTokens[0]?.account_id || 1}

# Database Configuration
MYSQL_HOST=localhost
MYSQL_PORT=3306
MYSQL_USER=root
MYSQL_PASSWORD=your_password
MYSQL_DB=converse_mcp`)}
                    >
                      Copy Configuration
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Audit Logs (now index 3) */}
          <TabPanel value={selectedTab} index={3}>
            <Grid container spacing={3}>
              {/* Audit Log Filters */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Activity & Audit Logs"
                    avatar={<History color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Track all user activities, system changes, and security events in your account.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                      <TextField
                        label="Search logs"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 200 }}
                        placeholder="Search by user, action, or IP..."
                      />
                      <TextField
                        label="Date Range"
                        type="date"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Action Type"
                        select
                        variant="outlined"
                        size="small"
                        defaultValue="all"
                        sx={{ minWidth: 120 }}
                      >
                        <option value="all">All Actions</option>
                        <option value="login">Logins</option>
                        <option value="api">API Calls</option>
                        <option value="settings">Settings</option>
                        <option value="tokens">API Tokens</option>
                      </TextField>
                      <Button variant="outlined" startIcon={<Refresh />}>
                        Refresh
                      </Button>
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {auditLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  {new Date(log.timestamp).toLocaleString()}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {log.user.charAt(0)}
                                  </Avatar>
                                  {log.user}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.action}
                                  size="small"
                                  color={
                                    log.action.includes('Login') ? 'primary' :
                                    log.action.includes('API') ? 'secondary' :
                                    log.action.includes('Settings') ? 'info' : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {log.ip}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.status}
                                  size="small"
                                  color={log.status === 'success' ? 'success' : 'error'}
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setSelectedAuditLog(log);
                                    setAuditLogDetailOpen(true);
                                  }}
                                >
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Security Overview */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader
                    title="Security Overview"
                    avatar={<Security color="primary" />}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Two-Factor Authentication"
                          secondary="Enabled"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="API Access Logging"
                          secondary="Active"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ErrorOutline color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Password Policy"
                          secondary="Review Recommended"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Activity Summary */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader
                    title="Activity Summary (Last 24 Hours)"
                    avatar={<Info color="primary" />}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">
                            {auditLogs.filter(log => log.action.includes('Login')).length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Logins
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="secondary">
                            {auditLogs.filter(log => log.action.includes('API')).length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            API Calls
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {auditLogs.filter(log => log.action.includes('Settings')).length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Config Changes
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {auditLogs.filter(log => log.status === 'success').length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Successful
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* SLA & Performance (now index 4) */}
          <TabPanel value={selectedTab} index={4}>
            <Grid container spacing={3}>
              {/* SLA Metrics Overview */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Service Level Agreement (SLA) Metrics"
                    avatar={<Speed color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Monitor your team's performance against SLA targets and customer satisfaction goals.
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {slaData.map((metric, index) => (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                          <Card variant="outlined">
                            <CardContent sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" gutterBottom>
                                {metric.metric}
                              </Typography>
                              <Typography variant="h4" sx={{ 
                                color: metric.status === 'excellent' ? 'success.main' : 
                                       metric.status === 'good' ? 'primary.main' : 'warning.main',
                                mb: 1
                              }}>
                                {metric.current}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Target: {metric.target}
                              </Typography>
                              
                              <LinearProgress 
                                variant="determinate" 
                                value={metric.percentage} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: metric.status === 'excellent' ? 'success.main' : 
                                                   metric.status === 'good' ? 'primary.main' : 'warning.main'
                                  }
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {metric.percentage}% of target
                              </Typography>
                              
                              <Chip
                                label={metric.status === 'excellent' ? 'Excellent' : 
                                       metric.status === 'good' ? 'On Track' : 'Needs Attention'}
                                size="small"
                                color={metric.status === 'excellent' ? 'success' : 
                                       metric.status === 'good' ? 'primary' : 'warning'}
                                sx={{ mt: 1 }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Analytics */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader
                    title="Performance Analytics"
                    action={
                      <Box>
                        <Button
                          variant={performanceDateRange === '7days' ? 'contained' : 'outlined'}
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => handleDateRangeChange('7days')}
                        >
                          Last 7 Days
                        </Button>
                        <Button
                          variant={performanceDateRange === '30days' ? 'contained' : 'outlined'}
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={() => handleDateRangeChange('30days')}
                        >
                          Last 30 Days
                        </Button>
                        <Button
                          variant={performanceDateRange === 'custom' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleDateRangeChange('custom')}
                        >
                          Custom Range
                        </Button>
                      </Box>
                    }
                  />
                  <CardContent>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body1" color="text.secondary">
                        📊 Performance Chart Placeholder
                        <br />
                        <Typography variant="body2" color="text.secondary">
                          Integration with charting library (Chart.js, Recharts, etc.) would go here
                        </Typography>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* SLA Configuration */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader
                    title="SLA Configuration"
                    avatar={<Business color="primary" />}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="First Response SLA"
                          secondary={`${slaConfig.firstResponseTime} minutes`}
                        />
                        <IconButton size="small" onClick={() => handleSLAConfigEdit('firstResponseTime')}>
                          <Edit />
                        </IconButton>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Resolution Time SLA"
                          secondary={`${slaConfig.resolutionTime} hours`}
                        />
                        <IconButton size="small" onClick={() => handleSLAConfigEdit('resolutionTime')}>
                          <Edit />
                        </IconButton>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Customer Satisfaction Target"
                          secondary={`${slaConfig.csatTarget}%`}
                        />
                        <IconButton size="small" onClick={() => handleSLAConfigEdit('csatTarget')}>
                          <Edit />
                        </IconButton>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Working Hours"
                          secondary={slaConfig.workingHours}
                        />
                        <IconButton size="small" onClick={() => handleSLAConfigEdit('workingHours')}>
                          <Edit />
                        </IconButton>
                      </ListItem>
                    </List>

                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ mt: 2 }}
                      startIcon={<Save />}
                      onClick={handleSaveSLAConfig}
                    >
                      Update SLA Settings
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Team Performance */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Team Performance Breakdown"
                    action={
                      <Button startIcon={<Visibility />}>
                        View Detailed Report
                      </Button>
                    }
                  />
                  <CardContent>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Agent</TableCell>
                            <TableCell>Conversations</TableCell>
                            <TableCell>Avg Response Time</TableCell>
                            <TableCell>Avg Resolution Time</TableCell>
                            <TableCell>Customer Rating</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {teamPerformanceData.length > 0 ? teamPerformanceData.map((agent, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 32, height: 32 }}>
                                    {agent.name.split(' ').map((n: string) => n[0]).join('')}
                                  </Avatar>
                                  {agent.name}
                                </Box>
                              </TableCell>
                              <TableCell>{agent.conversations}</TableCell>
                              <TableCell>{agent.responseTime}</TableCell>
                              <TableCell>{agent.resolutionTime}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  ⭐ {agent.rating}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={agent.status === 'excellent' ? 'Excellent' : 
                                         agent.status === 'good' ? 'Good' : 'Needs Improvement'}
                                  size="small"
                                  color={agent.status === 'excellent' ? 'success' : 
                                         agent.status === 'good' ? 'primary' : 'warning'}
                                />
                              </TableCell>
                            </TableRow>
                          )) : (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                  {loading ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    'No team performance data available for the selected date range.'
                                  )}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Help & Support (now index 5) */}
          <TabPanel value={selectedTab} index={5}>
            <Grid container spacing={3}>
              {/* Contact Support */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Contact Support"
                    avatar={<Help color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Need help? Get in touch with our support team for assistance with your Converse account.
                    </Typography>
                    
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ mb: 2 }}
                      onClick={() => setContactSupportOpen(true)}
                      startIcon={<Email />}
                    >
                      Submit Support Ticket
                    </Button>
                    
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      sx={{ mb: 2 }}
                      onClick={() => setScheduleDemoOpen(true)}
                      startIcon={<Business />}
                    >
                      Schedule Demo Call
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                      Quick Contact Options:
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <Email color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Email Support"
                          secondary="contact@mail.rentremote.com"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Phone color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Phone Support"
                          secondary="+1 (365) 517-0433"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Web color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Knowledge Base"
                          secondary={
                            <Link onClick={() => navigate('/dashboard/help')} sx={{ cursor: 'pointer' }}>
                              /help
                            </Link>
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* System Information */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="System Information"
                    avatar={<Info color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      System details that may be helpful when contacting support.
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Version"
                          secondary={systemInfo.version || 'Loading...'}
                        />
                        <Chip 
                          label={systemInfo.status === 'operational' ? 'Operational' : 'Loading'} 
                          color={systemInfo.status === 'operational' ? 'success' : 'default'} 
                          size="small" 
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Instance ID"
                          secondary={systemInfo.instanceId || 'Loading...'}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Region"
                          secondary={systemInfo.region || 'Loading...'}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText
                          primary="Account ID"
                          secondary={apiTokens[0]?.account_id || 'Not available'}
                        />
                      </ListItem>
                    </List>
                    
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      sx={{ mt: 2 }}
                      startIcon={<ContentCopy />}
                      onClick={() => copyToClipboard(`Version: ${systemInfo.version}\nInstance: ${systemInfo.instanceId}\nRegion: ${systemInfo.region}\nAccount: ${apiTokens[0]?.account_id || 'N/A'}`)}
                    >
                      Copy System Info
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Documentation & Resources */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Documentation & Resources"
                    avatar={<Description color="primary" />}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Description sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                              API Documentation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Complete API reference and integration guides
                            </Typography>
                            <Button variant="outlined" fullWidth onClick={() => navigate('/dashboard/api-docs')}>
                              View API Docs
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Help sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                              User Guides
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Step-by-step tutorials and best practices
                            </Typography>
                            <Button variant="outlined" fullWidth onClick={() => navigate('/dashboard/help')}>
                              Browse Guides
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Web sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                              Video Tutorials
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Learn through interactive video content
                            </Typography>
                            <Button variant="outlined" fullWidth>
                              Watch Videos
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Community & Updates */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Community & Updates"
                    avatar={<NotificationsActive color="primary" />}
                  />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Web color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Community Forum"
                          secondary="Connect with other Converse users"
                        />
                        <Button size="small">Visit</Button>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsActive color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Status Page"
                          secondary="Check system status and updates"
                        />
                        <Button size="small">Check</Button>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Description color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Changelog"
                          secondary="Latest features and improvements"
                        />
                        <Button size="small" onClick={() => navigate('/dashboard/changelog')}>View</Button>
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Quick Actions */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    title="Quick Actions"
                    avatar={<Speed color="primary" />}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Common support tasks and troubleshooting steps.
                    </Typography>
                    
                    <List>
                      <ListItem button onClick={() => navigate('/dashboard/my-profile')}>
                        <ListItemIcon>
                          <AccountCircle />
                        </ListItemIcon>
                        <ListItemText
                          primary="Update Profile"
                          secondary="Change your account information"
                        />
                      </ListItem>
                      <ListItem button onClick={() => navigate('/dashboard/my-profile?tab=security')}>
                        <ListItemIcon>
                          <Security />
                        </ListItemIcon>
                        <ListItemText
                          primary="Reset Password"
                          secondary="Change your account password"
                        />
                      </ListItem>
                      <ListItem button>
                        <ListItemIcon>
                          <Refresh />
                        </ListItemIcon>
                        <ListItemText
                          primary="Clear Cache"
                          secondary="Refresh your session data"
                        />
                      </ListItem>
                      <ListItem button onClick={() => navigate('/dashboard/settings?tab=0')}>
                        <ListItemIcon>
                          <Key />
                        </ListItemIcon>
                        <ListItemText
                          primary="Regenerate API Keys"
                          secondary="Create new API tokens"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Co-Pilot Settings (now index 6) */}
          <TabPanel value={selectedTab} index={6}>
            <CoPilotSettingsTab />
          </TabPanel>

          {/* AI Configuration (now index 7) */}
          <TabPanel value={selectedTab} index={7}>
            <AIConfigPanel />
          </TabPanel>

        </Box>
      </Paper>

      {/* Support Ticket Dialog */}
      <Dialog
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Support Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide details about your issue and our support team will get back to you shortly.
          </Typography>

          <TextField
            fullWidth
            label="Your Name"
            id="ticket-name"
            sx={{ mb: 2 }}
            defaultValue={user?.full_name || ''}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            id="ticket-email"
            sx={{ mb: 2 }}
            defaultValue={user?.email || ''}
          />

          <TextField
            fullWidth
            select
            label="Category"
            id="ticket-category"
            defaultValue="general"
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="general">General Inquiry</option>
            <option value="bug">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="question">Question</option>
            <option value="technical_issue">Technical Issue</option>
            <option value="account_issue">Account Issue</option>
            <option value="other">Other</option>
          </TextField>

          <TextField
            fullWidth
            select
            label="Priority"
            id="ticket-priority"
            defaultValue="medium"
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </TextField>

          <TextField
            fullWidth
            label="Subject"
            id="ticket-subject"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            id="ticket-description"
            multiline
            rows={6}
            placeholder="Please describe your issue in detail..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactSupportOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const nameInput = document.getElementById('ticket-name') as HTMLInputElement;
              const emailInput = document.getElementById('ticket-email') as HTMLInputElement;
              const categoryInput = document.getElementById('ticket-category') as HTMLSelectElement;
              const priorityInput = document.getElementById('ticket-priority') as HTMLSelectElement;
              const subjectInput = document.getElementById('ticket-subject') as HTMLInputElement;
              const descriptionInput = document.getElementById('ticket-description') as HTMLTextAreaElement;

              try {
                setSaving(true);
                const response = await api.post('/support/ticket', {
                  name: nameInput?.value,
                  email: emailInput?.value,
                  category: categoryInput?.value,
                  priority: priorityInput?.value,
                  subject: subjectInput?.value,
                  description: descriptionInput?.value
                });

                setSnackbarMessage(`Support ticket created! Ticket number: ${response.data.ticket_number}`);
                setSnackbarOpen(true);
                setContactSupportOpen(false);

                // Clear form
                if (subjectInput) subjectInput.value = '';
                if (descriptionInput) descriptionInput.value = '';
              } catch (error: any) {
                setSnackbarMessage('Failed to create support ticket. Please try again.');
                setSnackbarOpen(true);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Demo Dialog */}
      <Dialog
        open={scheduleDemoOpen}
        onClose={() => setScheduleDemoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Demo Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Schedule a personalized demo to see how Converse can transform your customer support.
          </Typography>

          <TextField
            fullWidth
            label="Your Name"
            id="demo-name"
            sx={{ mb: 2, mt: 1 }}
            defaultValue={user?.full_name || ''}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            id="demo-email"
            sx={{ mb: 2 }}
            defaultValue={user?.email || ''}
          />

          <TextField
            fullWidth
            label="Company"
            id="demo-company"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone (Optional)"
            type="tel"
            id="demo-phone"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Preferred Date"
            type="date"
            id="demo-date"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Preferred Time (Optional)"
            type="time"
            id="demo-time"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Additional Message (Optional)"
            id="demo-message"
            multiline
            rows={4}
            placeholder="Let us know if you have any specific topics or features you'd like to discuss..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDemoOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const nameInput = document.getElementById('demo-name') as HTMLInputElement;
              const emailInput = document.getElementById('demo-email') as HTMLInputElement;
              const companyInput = document.getElementById('demo-company') as HTMLInputElement;
              const phoneInput = document.getElementById('demo-phone') as HTMLInputElement;
              const dateInput = document.getElementById('demo-date') as HTMLInputElement;
              const timeInput = document.getElementById('demo-time') as HTMLInputElement;
              const messageInput = document.getElementById('demo-message') as HTMLTextAreaElement;

              if (!nameInput?.value || !emailInput?.value || !companyInput?.value || !dateInput?.value) {
                setSnackbarMessage('Please fill in all required fields');
                setSnackbarOpen(true);
                return;
              }

              try {
                setSaving(true);
                const response = await api.post('/support/demo', {
                  name: nameInput.value,
                  email: emailInput.value,
                  company: companyInput.value,
                  phone: phoneInput?.value || null,
                  preferred_date: dateInput.value,
                  preferred_time: timeInput?.value || null,
                  message: messageInput?.value || null
                });

                setSnackbarMessage(`Demo scheduled! Reference: ${response.data.booking_reference}`);
                setSnackbarOpen(true);
                setScheduleDemoOpen(false);

                // Clear form
                if (companyInput) companyInput.value = '';
                if (phoneInput) phoneInput.value = '';
                if (dateInput) dateInput.value = '';
                if (timeInput) timeInput.value = '';
                if (messageInput) messageInput.value = '';
              } catch (error: any) {
                setSnackbarMessage('Failed to schedule demo. Please try again.');
                setSnackbarOpen(true);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Schedule Demo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SLA Configuration Edit Dialog */}
      <Dialog
        open={slaConfigDialogOpen}
        onClose={() => setSlaConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit SLA Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update your Service Level Agreement targets
          </Typography>

          {editingSlaField === 'firstResponseTime' && (
            <TextField
              fullWidth
              label="First Response Time (minutes)"
              type="number"
              value={slaConfig.firstResponseTime}
              onChange={(e) => setSlaConfig({ ...slaConfig, firstResponseTime: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Target time for first agent response"
            />
          )}

          {editingSlaField === 'resolutionTime' && (
            <TextField
              fullWidth
              label="Resolution Time (hours)"
              type="number"
              value={slaConfig.resolutionTime}
              onChange={(e) => setSlaConfig({ ...slaConfig, resolutionTime: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Target time to resolve conversations"
            />
          )}

          {editingSlaField === 'csatTarget' && (
            <TextField
              fullWidth
              label="Customer Satisfaction Target (%)"
              type="number"
              value={slaConfig.csatTarget}
              onChange={(e) => setSlaConfig({ ...slaConfig, csatTarget: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Target CSAT score percentage"
              inputProps={{ min: 0, max: 100 }}
            />
          )}

          {editingSlaField === 'workingHours' && (
            <TextField
              fullWidth
              label="Working Hours"
              value={slaConfig.workingHours}
              onChange={(e) => setSlaConfig({ ...slaConfig, workingHours: e.target.value })}
              sx={{ mt: 2 }}
              helperText="Format: 9 AM - 6 PM (UTC)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlaConfigDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSLAConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SettingsPage;